import React, { useState } from 'react'
import { Link } from 'react-router'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRight, BarChart3, NotebookPen, ShieldPlus, Users } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import LanguageToggle from '../components/LanguageToggle'
import UserEmailBadge from '../components/UserEmailBadge'
import CreateAdminModal from '../components/CreateAdminModal'

const AdminDashboard = () => {
  const { user, signOut } = useAuth()
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const [showCreateAdminModal, setShowCreateAdminModal] = useState(false)

  const handleLogout = async () => {
    const { error } = await signOut()
    if (error) {
      console.error('Error signing out:', error)
    }
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <h1 className="text-3xl font-bold text-gray-900">
              {t('dashboard.title')}
            </h1>
            <div className="flex items-center space-x-4">
              <LanguageToggle />
              <UserEmailBadge email={user?.email} />
              <button
                onClick={handleLogout}
                className="bg-[#FF1B54] hover:bg-[#E1174C] text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Customer Management Card */}
            <div className="bg-white border border-gray-100 overflow-hidden shadow-sm rounded-2xl hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center shadow-sm">
                      <Users className="w-6 h-6 text-white" aria-hidden="true" />
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-500">
                      {t('navigation.customerManagement')}
                    </div>
                    <div className="mt-1 text-xl font-semibold text-gray-900">
                      {t('customers.title')}
                    </div>
                    <div className="mt-1 text-sm text-gray-600">
                      {t('customers.subtitle')}
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
                <Link
                  to="/admin/customers"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-900"
                >
                  {t('customers.title')}
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Portfolio Analytics Card */}
            <div className="bg-white border border-gray-100 overflow-hidden shadow-sm rounded-2xl hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center shadow-sm">
                      <BarChart3 className="w-6 h-6 text-white" aria-hidden="true" />
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-500">
                      {t('navigation.portfolioAnalytics')}
                    </div>
                    <div className="mt-1 text-xl font-semibold text-gray-900">
                      {t('analytics.title')}
                    </div>
                    <div className="mt-1 text-sm text-gray-600">
                      {t('analytics.subtitle')}
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
                <Link
                  to="/admin/analytics"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-green-700 hover:text-green-900"
                >
                  {t('actions.viewAnalytics')}
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Notes & Insights Card */}
            <div className="bg-white border border-gray-100 overflow-hidden shadow-sm rounded-2xl hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center shadow-sm">
                      <NotebookPen className="w-6 h-6 text-white" aria-hidden="true" />
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-500">
                      {t('navigation.customerInsights')}
                    </div>
                    <div className="mt-1 text-xl font-semibold text-gray-900">
                      {t('notes.title')}
                    </div>
                    <div className="mt-1 text-sm text-gray-600">
                      {t('notes.subtitle')}
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-purple-700 hover:text-purple-900"
                  onClick={() => {
                    // TODO: wire up to an Admin Notes route/panel when available
                  }}
                >
                  {t('notes.title')}
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
            </div>

            {/* Admin Management Card */}
            <div className="bg-white border border-gray-100 overflow-hidden shadow-sm rounded-2xl hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center shadow-sm">
                      <ShieldPlus className="w-6 h-6 text-white" aria-hidden="true" />
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-500">
                      {t('adminManagement.title')}
                    </div>
                    <div className="mt-1 text-xl font-semibold text-gray-900">
                      {t('adminManagement.createTitle')}
                    </div>
                    <div className="mt-1 text-sm text-gray-600">
                      {t('adminManagement.subtitle')}
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
                <button
                  type="button"
                  onClick={() => setShowCreateAdminModal(true)}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 hover:text-indigo-900"
                >
                  {t('adminManagement.createAction')}
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <CreateAdminModal
        isOpen={showCreateAdminModal}
        onClose={() => setShowCreateAdminModal(false)}
        onSuccess={() => {
          // For now, no list to refresh; placeholder for future enhancements
          console.log('Admin created successfully')
        }}
      />
    </div>
  )
}

export default AdminDashboard
